/**
 * B28 — §8.1 ticket tokens. Compact, asymmetric: validators hold only
 * public keys; the core (or a synced manifest) is the only authority on
 * remaining uses, so the payload structurally CANNOT carry balance.
 *
 * Format (static in Phase 1; rotating barcodes slot into the same field):
 *
 *   ttk1.<base64url(canonical payload JSON)>.<keyId>.<base64url(signature)>
 *
 * The signature covers `ttk1.<payload-b64>.<keyId>` — version and key id
 * are inside the signed bytes, so neither can be swapped on a real token.
 * Canonical JSON = fixed field order, no undefined — minting is
 * deterministic and the bytes verified are the bytes signed.
 *
 * PURITY: signing/verification are injected ports (node:crypto lives in
 * the composition roots and tests, never here). Parse is fail-closed:
 * garbage returns a refusal, extra fields are malformed, nothing throws.
 */

import type { Entitlement } from "./entitlement.js";

/* ---------------------------------------------------------------- types */

export interface TicketPayload {
  readonly tenantId: string;
  readonly entitlementId: string;
  readonly productId: string;
  /** The slot binding, when the entitlement is slot-bound (§8.1). */
  readonly slotId?: string;
  /** Validity window (epoch ms), when known at issuance. */
  readonly validFrom?: number;
  readonly validUntil?: number;
}

/** Signs canonical token bytes under a named key (rotation seam). */
export interface TicketSigner {
  readonly keyId: string;
  sign(bytes: Uint8Array): Uint8Array | Promise<Uint8Array>;
}

export type TicketVerifyPortResult = "ok" | "bad_signature" | "unknown_key";

/** Checks a signature under a named public key — and ONLY public keys. */
export interface TicketVerifierPort {
  verify(
    bytes: Uint8Array,
    signature: Uint8Array,
    keyId: string,
  ): TicketVerifyPortResult | Promise<TicketVerifyPortResult>;
}

export type TicketRefusal = "malformed" | "bad_signature" | "unknown_key";

export type ParsedTicket =
  | { readonly ok: true; readonly payload: TicketPayload; readonly keyId: string; readonly signedBytes: Uint8Array; readonly signature: Uint8Array }
  | { readonly ok: false; readonly why: "malformed" };

export type VerifiedTicket =
  | { readonly ok: true; readonly keyId: string; readonly payload: TicketPayload }
  | { readonly ok: false; readonly why: TicketRefusal };

export class InvalidTicketPayloadError extends Error {
  constructor(why: string) {
    super(`Refusing to mint a ticket token: ${why}`);
    this.name = "InvalidTicketPayloadError";
  }
}

/* ------------------------------------------------------------ base64url */

const B64 = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
const B64_INDEX = new Map<string, number>([...B64].map((c, i) => [c, i]));

export function toBase64Url(bytes: Uint8Array): string {
  let out = "";
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i]!;
    const b = bytes[i + 1];
    const c = bytes[i + 2];
    out += B64[a >> 2]! + B64[((a & 3) << 4) | ((b ?? 0) >> 4)]!;
    if (b !== undefined) out += B64[((b & 15) << 2) | ((c ?? 0) >> 6)]!;
    if (c !== undefined) out += B64[c & 63]!;
  }
  return out;
}

export function fromBase64Url(text: string): Uint8Array | undefined {
  if (text.length % 4 === 1) return undefined;
  const bytes: number[] = [];
  let buffer = 0;
  let bits = 0;
  for (const ch of text) {
    const value = B64_INDEX.get(ch);
    if (value === undefined) return undefined;
    buffer = (buffer << 6) | value;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      bytes.push((buffer >> bits) & 0xff);
    }
  }
  // Canonical only: leftover bits must be zero and re-encoding must give
  // back the input — otherwise distinct strings alias one byte sequence
  // (malleable tokens; the tamper property catches exactly this).
  if (bits > 0 && (buffer & ((1 << bits) - 1)) !== 0) return undefined;
  const out = Uint8Array.from(bytes);
  if (toBase64Url(out) !== text) return undefined;
  return out;
}

/* -------------------------------------------------------------- payload */

const PAYLOAD_KEYS = ["tenantId", "entitlementId", "productId", "slotId", "validFrom", "validUntil"] as const;

function assertPayload(payload: TicketPayload): void {
  for (const field of ["tenantId", "entitlementId", "productId"] as const) {
    const v = payload[field];
    if (typeof v !== "string" || v.trim().length === 0) throw new InvalidTicketPayloadError(`${field} is required`);
  }
  if (payload.slotId !== undefined && payload.slotId.trim().length === 0)
    throw new InvalidTicketPayloadError("slotId, when present, must be non-empty");
  for (const field of ["validFrom", "validUntil"] as const) {
    const v = payload[field];
    if (v !== undefined && (!Number.isSafeInteger(v) || v < 0))
      throw new InvalidTicketPayloadError(`${field} must be a non-negative epoch-ms integer`);
  }
  if (payload.validFrom !== undefined && payload.validUntil !== undefined && payload.validUntil < payload.validFrom)
    throw new InvalidTicketPayloadError("validUntil precedes validFrom");
}

/** Canonical form: fixed key order, undefined omitted — deterministic bytes. */
function canonicalPayloadJson(payload: TicketPayload): string {
  const ordered: Record<string, unknown> = {};
  for (const key of PAYLOAD_KEYS) {
    const value = payload[key];
    if (value !== undefined) ordered[key] = value;
  }
  return JSON.stringify(ordered);
}

function parsePayload(json: string): TicketPayload | undefined {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return undefined;
  }
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const record = raw as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (!(PAYLOAD_KEYS as readonly string[]).includes(key)) return undefined; // extras (e.g. smuggled balance) are malformed
  }
  const out: Record<string, unknown> = {};
  for (const field of ["tenantId", "entitlementId", "productId"] as const) {
    const v = record[field];
    if (typeof v !== "string" || v.trim().length === 0) return undefined;
    out[field] = v;
  }
  if (record["slotId"] !== undefined) {
    if (typeof record["slotId"] !== "string" || record["slotId"].trim().length === 0) return undefined;
    out["slotId"] = record["slotId"];
  }
  for (const field of ["validFrom", "validUntil"] as const) {
    const v = record[field];
    if (v !== undefined) {
      if (typeof v !== "number" || !Number.isSafeInteger(v) || v < 0) return undefined;
      out[field] = v;
    }
  }
  const payload = out as unknown as TicketPayload;
  if (payload.validFrom !== undefined && payload.validUntil !== undefined && payload.validUntil < payload.validFrom)
    return undefined;
  return payload;
}

/* ----------------------------------------------------------------- api */

const VERSION = "ttk1";
const encoder = new TextEncoder();
const decoder = new TextDecoder();

export async function mintTicketToken(payload: TicketPayload, signer: TicketSigner): Promise<string> {
  assertPayload(payload);
  if (signer.keyId.trim().length === 0 || signer.keyId.includes(".")) {
    throw new InvalidTicketPayloadError("signer keyId must be non-empty and dot-free");
  }
  const body = toBase64Url(encoder.encode(canonicalPayloadJson(payload)));
  const signedBytes = encoder.encode(`${VERSION}.${body}.${signer.keyId}`);
  const signature = await signer.sign(signedBytes);
  return `${VERSION}.${body}.${signer.keyId}.${toBase64Url(signature)}`;
}

export function parseTicketToken(token: string): ParsedTicket {
  const malformed = { ok: false, why: "malformed" } as const;
  const parts = token.split(".");
  if (parts.length !== 4) return malformed;
  const [version, body, keyId, sig] = parts as [string, string, string, string];
  if (version !== VERSION || body.length === 0 || keyId.length === 0 || sig.length === 0) return malformed;
  const payloadBytes = fromBase64Url(body);
  const signature = fromBase64Url(sig);
  if (payloadBytes === undefined || signature === undefined || signature.length === 0) return malformed;
  const payload = parsePayload(decoder.decode(payloadBytes));
  if (payload === undefined) return malformed;
  // Re-encoding must reproduce the wire bytes exactly — a token that is not
  // ITS OWN canonical form is refused (no malleable duplicates of one ticket).
  if (toBase64Url(encoder.encode(canonicalPayloadJson(payload))) !== body) return malformed;
  return { ok: true, payload, keyId, signedBytes: encoder.encode(`${VERSION}.${body}.${keyId}`), signature };
}

export async function verifyTicketToken(token: string, verifier: TicketVerifierPort): Promise<VerifiedTicket> {
  const parsed = parseTicketToken(token);
  if (!parsed.ok) return { ok: false, why: parsed.why };
  const result = await verifier.verify(parsed.signedBytes, parsed.signature, parsed.keyId);
  if (result !== "ok") return { ok: false, why: result };
  return { ok: true, keyId: parsed.keyId, payload: parsed.payload };
}

/**
 * The one payload an entitlement's token carries — the delivery email and
 * the slot manifest both mint from HERE, so (Ed25519 being deterministic)
 * the QR in the inbox and the manifest row are byte-equal strings. The slot
 * binding is stamped only when it is unambiguous: a multi-leg journey lists
 * several slots, and a token must not claim one of them arbitrarily (§8.1).
 */
export function ticketPayloadFor(entitlement: Entitlement): TicketPayload {
  return {
    tenantId: entitlement.tenantId,
    entitlementId: entitlement.entitlementId,
    productId: entitlement.productId,
    // B56 (E1): slotIds are stamped ORIGIN FIRST at issuance, so the token
    // binds the BOARDING slot — a single slot itself, a journey its origin
    // leg (§8.2 wrong_leg keys off this binding at the gate).
    ...(entitlement.slotIds !== undefined && entitlement.slotIds.length > 0
      ? { slotId: entitlement.slotIds[0] }
      : {}),
    ...(entitlement.validity.start !== undefined ? { validFrom: entitlement.validity.start } : {}),
    validUntil: entitlement.validity.end,
  };
}
